import * as api from '@gitbook/api-client';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { assertNever } from '@gitbook/util-ts';

import publicIllustration from './assets/site-visibility-public.raw.svg';
import shareLink from './assets/site-visibility-share-links.raw.svg';
import visitorAuth from './assets/site-visibility-va.raw.svg';

/** Visibility label */
const SiteVisibilityLabels: Record<api.SiteVisibility, string> = {
    [api.SiteVisibility.Public]: 'Public',
    [api.SiteVisibility.ShareLink]: 'Share links',
    [api.SiteVisibility.VisitorAuth]: 'Authenticated access',
    [api.SiteVisibility.Unlisted]: 'Unlisted',
};

const ShortSiteVisibilityLabels = {
    [api.SiteVisibility.VisitorAuth]: 'Authenticated access',
};

export function getSiteVisibilityLabel(
    visibility: api.SiteVisibility,
    options?: { short?: boolean }
): string {
    if (options?.short && ShortSiteVisibilityLabels[visibility]) {
        return ShortSiteVisibilityLabels[visibility];
    }
    return SiteVisibilityLabels[visibility];
}

/** Visibility icons */
const SiteVisibilityIcons: Record<api.SiteVisibility, SVGIconComponent> = {
    [api.SiteVisibility.Public]: Icon.PublishStatusPublic,
    [api.SiteVisibility.ShareLink]: Icon.PublishStatusShareLink,
    [api.SiteVisibility.VisitorAuth]: Icon.PublishStatusVisitorAuth,
    [api.SiteVisibility.Unlisted]: Icon.PublishStatusUnlisted,
};

export function getSiteVisibilityIcon(visibility: api.SiteVisibility): SVGIconComponent {
    return SiteVisibilityIcons[visibility];
}

/** Visibility illustration */
export function getSiteVisibilityIllustration(visibility: api.SiteVisibility): string {
    switch (visibility) {
        case api.SiteVisibility.Public:
        case api.SiteVisibility.Unlisted:
            return publicIllustration;
        case api.SiteVisibility.ShareLink:
            return shareLink;
        case api.SiteVisibility.VisitorAuth:
            return visitorAuth;
        default:
            assertNever(visibility);
    }
}

/** Upgrade actions for visibility */
export const getUpgradeKeyForVisibility = (visibility: api.SiteVisibility): api.SiteFeatureKey => {
    switch (visibility) {
        case api.SiteVisibility.Public:
            return api.SiteFeatureKey.SitesPublicVisibility;
        case api.SiteVisibility.ShareLink:
            return api.SiteFeatureKey.SitesShareLinks;
        case api.SiteVisibility.VisitorAuth:
            return api.SiteFeatureKey.SitesVisitorAuthentication;
        case api.SiteVisibility.Unlisted:
            return api.SiteFeatureKey.SitesUnlistedVisibility;
        default:
            assertNever(visibility);
    }
};

/** Visibility options for the site audience picker */
export const siteVisibilityOptions = {
    [api.SiteVisibility.Public]: {
        label: getSiteVisibilityLabel(api.SiteVisibility.Public),
        value: api.SiteVisibility.Public,
        icon: Icon.PublishStatusPublic,
    },
    [api.SiteVisibility.ShareLink]: {
        label: getSiteVisibilityLabel(api.SiteVisibility.ShareLink),
        value: api.SiteVisibility.ShareLink,
        icon: Icon.PublishStatusShareLink,
    },
    [api.SiteVisibility.VisitorAuth]: {
        label: getSiteVisibilityLabel(api.SiteVisibility.VisitorAuth),
        value: api.SiteVisibility.VisitorAuth,
        icon: Icon.PublishStatusVisitorAuth,
    },
};
